import { Select, TextArea } from '@/components/DesignLibrary/Atoms';
import Button from '@/components/DesignLibrary/Button';
import CopyButton from '@/components/CopyButton';
import Copy from '@/components/Icons/Copy';
import PencilSimple from '@/components/Icons/PencilSimple';
import PlusCircle from '@/components/Icons/PlusCircle';
import { FC, useEffect, useState, useMemo } from 'react';
import { IBM_Plex_Mono as ibmPlexMono } from 'next/font/google';
import Expression from '@/helpers/expressions/ThisExpression';
import CollapsibleArea from '@/components/CollapsibleArea';
import { TBody, TD, TH, Table, TableRow, THead } from '@/components/DesignLibrary/Table';
import Buttons from '@/components/Buttons';
import TextInput from '@/components/FormFields/TextInput';
import Modal from '@/components/Modal';

import { parseConditions } from './utils';
import s from './ExpressionEditor.module.scss';

const mono = ibmPlexMono({
  subsets: ['latin'],
  weight: '400',
});

const escapeRegExp = (string: string): string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

type FieldInfo = {
  key: string;
  label: string;
  type: string;
  selector?: string;
  options?: { key: string; label: string }[];
};

type VariableReferenceType = Expression['variables'][number]['referenceType'];

const ComplexEditor: FC<{
  expression: Expression;
  onChange?: (expression: Expression) => void;
  onSave: () => void;
  canEditVars?: boolean;
  canEditFilterExpressionProperties?: boolean;
  fieldsInfo: Record<string, FieldInfo>;
}> = ({ expression, onChange, onSave, canEditVars, canEditFilterExpressionProperties, fieldsInfo }) => {
  const [editVariable, setEditVariable] = useState<string | null>(null);
  const [vName, setVName] = useState<string>('');
  const [vNameError, setVNameError] = useState<string | null>(null);
  const [vReferenceType, setVReferenceType] = useState<VariableReferenceType | ''>('');
  const [vReferenceId, setVReferenceId] = useState<string>('');
  const [vSelector, setVSelector] = useState<string>('');
  const [expressionError, setExpressionError] = useState<string | null>(null);

  const fieldOptions: FieldInfo[] = useMemo(
    () =>
      Object.values(fieldsInfo).map(field => ({
        key: field.key,
        label: field.label,
        selector: field.selector,
        type: field.type,
      })),
    [fieldsInfo],
  );

  const systemVariables = fieldOptions.map(option => ({
    referenceId: option.key.includes('context') ? 'self' : '',
    referenceType: option.key.includes('context') ? 'workflow-context' as VariableReferenceType : '' as const,
    selector: option.selector || "",
    variableName: option.key,
  }));

  function extractVariableNames(expressionString: string): string[] {
    const variableSet = new Set<string>();
    const variableRegex = /\b[a-zA-Z_][a-zA-Z0-9_]*\b/g;
    let match = variableRegex.exec(expressionString);
    while (match !== null) {
      variableSet.add(match[0]);
      match = variableRegex.exec(expressionString);
    }
    return Array.from(variableSet);
  }

  useEffect(() => {
    if (editVariable !== 'newVariable') {
      const variable = expression.variables.find(v => v.variableName === editVariable);
      setVName(variable?.variableName || '');
      setVReferenceId(variable?.referenceId || '');
      setVSelector(variable?.selector || '');
      setVReferenceType(variable?.referenceType ?? '');
    }
  }, [editVariable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((expression.variables.some(v => v.variableName === vName)) && editVariable !== vName) {
      setVNameError('Variable name must be unique');
    } else {
      setVNameError(null);
    }
  }, [vName, expression.variables, editVariable]);

  const handleChangeExpressionText = (value: string): void => {
    if (!onChange) {
      return;
    }
    try {
      parseConditions(value);
      setExpressionError(null);

      const variablesInExpression = extractVariableNames(value);

      const newVariables = variablesInExpression
        .filter(varName => systemVariables.some(sv => sv.variableName === varName))
        .filter(varName => !expression.variables.some(ev => ev.variableName === varName))
        .map(varName => {
          const systemVar = systemVariables.find(sv => sv.variableName === varName);
          return {
            referenceId: systemVar?.referenceId || '',
            referenceType: systemVar?.referenceType || 'workflow-context' as VariableReferenceType,
            selector: systemVar?.selector || '',
            variableName: varName,
          };
        });

      onChange({
        ...expression,
        expression: value,
        variables: [...expression.variables, ...newVariables],
      });
    } catch (error) {
      if (error
        && typeof error === 'object'
        && 'description' in error
        && typeof error.description === 'string'
      ) {
        setExpressionError((error as { index: number; description: string }).description);
      } else if (error instanceof Error) {
        setExpressionError(error.message);
      } else {
        console.error(error);
        setExpressionError('Failed to parse expression');
      }

      onChange({
        ...expression,
        expression: value,
        variables: expression.variables,
      });
    }
  };

  const handleSaveVariable = (): void => {
    if (!onChange || !editVariable) {
      return;
    }

    if (vReferenceType && editVariable === 'newVariable') {
      onChange({
        ...expression,
        variables: [
          ...expression.variables,
          {
            referenceId: vReferenceId,
            referenceType: vReferenceType,
            selector: vSelector,
            variableName: vName,
          },
        ],
      });
    } else {

      const escapedVariableName = escapeRegExp(editVariable);

      const updatedExpression = expression.expression.replace(new RegExp(escapedVariableName, 'g'), vName);

      onChange({
        ...expression,
        expression: updatedExpression,
        variables: expression.variables.map(v => (v.variableName === editVariable
          ? { ...v,
            referenceId: vReferenceId,
            referenceType: vReferenceType as VariableReferenceType, // @todo check type
            selector: vSelector,
            variableName: vName,
          }
          : v)),
      });
    }
    setEditVariable(null);
  };

  const handleDeleteVariable = (): void => {
    if (!onChange) {
      return;
    }

    onChange({
      ...expression,
      variables: expression.variables.filter(v => v.variableName !== editVariable),
    });
    setEditVariable(null);
  };

  const canEdit = Boolean(onChange);

  return (
    <>
      <TextArea
        label=""
        value={expression.expression || ''}
        rows={10}
        disabled={!canEdit}
        onChange={handleChangeExpressionText}
        style={mono.style}
        error={expressionError || ''}
        optional
      />
      {/* List of variables */}
      <div className={s.variables}>
        {(
          <>
            {canEditVars && canEdit && (
              <Buttons
                iconBefore={<PlusCircle size={16}
                  weight="fill" />}
                onClick={() => setEditVariable('newVariable')}
              >
                Add Variable
              </Buttons>
            )}
            <CollapsibleArea title={`Expression Variables (${expression.variables.length})`} caretPosition="left">
              <Table>
                <THead>
                  <TableRow>
                    <TH label="Variable Name" />
                    <TH label="Reference ID" />
                    <TH label="Selector" />
                    <TH label="Reference Type" />
                    <TH label="" />
                  </TableRow>
                </THead>
                <TBody>
                  {expression.variables
                    .map(({ variableName, referenceType, referenceId, selector }) => (
                      <TableRow key={variableName}>
                        <TD>
                          <CopyButton text={variableName} className={s.copyVar}>
                            <Copy size={12} />
                            {variableName}
                          </CopyButton>
                        </TD>
                        <TD label={referenceId || '—'} compact />
                        <TD label={selector || '—'} compact />
                        <TD label={referenceType || '—'} compact />
                        <TD>
                          {canEditVars && canEdit && (
                            <Buttons isWrapper onClick={() => setEditVariable(variableName)}>
                              <PencilSimple size={12} weight="fill" />
                            </Buttons>
                          )}
                        </TD>
                      </TableRow>
                    ))
                  }
                </TBody>
              </Table>
            </CollapsibleArea>
          </>
        )}
        <CollapsibleArea title={`Available Variables (${systemVariables.length})`} caretPosition="left">
          <Table>
            <THead>
              <TableRow>
                <TH label="Variable Name" />
                {canEditVars && <>
                  <TH label="Reference ID" />
                  <TH label="Selector" />
                  <TH label="Reference Type" />
                </>}
              </TableRow>
            </THead>
            <TBody>
              {systemVariables
                .map(({ variableName, referenceType, referenceId, selector }) => (
                  <TableRow key={variableName}>
                    <TD>
                      <CopyButton text={variableName} className={s.copyVar}>
                        <Copy size={12} />
                        {variableName}
                      </CopyButton>
                    </TD>
                    {canEditVars && (
                      <>
                        <TD label={referenceId || '—'} compact />
                        <TD label={selector || '—'} compact />
                        <TD label={referenceType || '—'} compact />
                      </>
                    )}
                  </TableRow>
                ))}
            </TBody>
          </Table>
        </CollapsibleArea>
        {canEditFilterExpressionProperties && <CollapsibleArea title={`Filter Expression Properties`} caretPosition="left">
          <div className={s.filterExpressionProperties}>
            <TextArea
              label=""
              description="filterExpression"
              value={expression.filterExpression || ''}
              rows={5}
              disabled={!canEdit}
              onChange={(value) => {
                if (onChange) {
                  onChange({
                    ...expression,
                    filterExpression: value,
                  });
                }
              }}
              optional
            />
            <TextInput
              id='filterExpressionVariableName'
              value={expression.filterExpressionVariableName || ''}
              label='filterExpressionVariableName'
              disabled={!canEdit}
              onChange={(value) => {
                if (onChange) {
                  onChange({
                    ...expression,
                    filterExpressionVariableName: value,
                  });
                }
              }}
            />
            <TextInput
              id='filterExpressionOperand'
              value={expression.filterExpressionOperand || ''}
              label='filterExpressionOperand'
              disabled={!canEdit}
              onChange={(value) => {
                if (onChange) {
                  onChange({
                    ...expression,
                    filterExpressionOperand: value,
                  });
                }
              }}
            />
            <TextInput
              id='filterExpressionItemSelector'
              value={expression.filterExpressionItemSelector || ''}
              label='filterExpressionItemSelector'
              disabled={!canEdit}
              onChange={(value) => {
                if (onChange) {
                  onChange({
                    ...expression,
                    filterExpressionItemSelector: value,
                  });
                }
              }}
            />
            <Select
              label="filterExpressionReducer"
              compact
              options={[
                { key: 'sum', label: 'Sum' },
                { key: 'null', label: 'Null' },
              ]}
              value={expression.filterExpressionReducer === null ? 'null' : expression.filterExpressionReducer}
              onChange={(value) => {
                if (onChange) {
                  onChange({
                    ...expression,
                    filterExpressionReducer: value as 'sum' | null,
                  });
                }
              }}
              optional
            />
          </div>
        </CollapsibleArea>}
      </div>
      <Button
        variant="primary"
        size="md"
        onClick={onSave}
        disabled={!!expressionError || !canEdit}
      >
        Done
      </Button>
      {/* Edit variable modal */}
      {editVariable && canEdit && (
        <Modal hasUnsavedChanges title={`${editVariable === 'newVariable' ? 'Create' : 'Edit'} variable`} onClose={() => setEditVariable(null)}>
          <div className={s.editVar}>
            <TextInput
              id="variableName"
              label="Variable Name"
              value={vName}
              onChange={value => {
                setVName(value);
              }}
              error={vNameError}
              required
            />
            <TextInput id="variableReferenceId" label="Reference ID" value={vReferenceId} onChange={value => setVReferenceId(value)} />
            <TextInput id="variableSelector" label="Selector" value={vSelector} onChange={value => setVSelector(value)} />
            <TextInput id="variableReferenceType" label="Reference Type" value={vReferenceType} required onChange={value => setVReferenceType(value as VariableReferenceType)} />
            <Button
              variant="primary"
              size="md"
              disabled={!!vNameError || !vName || !vReferenceType}
              onClick={handleSaveVariable}
            >
              Save Variable
            </Button>
            {vName && <Button
              variant="destructive"
              size="md"
              onClick={handleDeleteVariable}
            >
              Delete Variable
            </Button>}
          </div>
        </Modal>
      )}
    </>
  );
};

export default ComplexEditor;
